import {defineComponent, nextTick, onBeforeUnmount, onMounted, ref, toRefs,} from 'vue';
import {track} from '../tracking/trackingLayer.js';
import EventBus from '../../scripts/lib/EventBus.js';

const templateElement = document.getElementById('add-to-cart-button-template');

export default defineComponent({
  template: templateElement ? templateElement.innerHTML : '',

  props: {
    syan: {
      type: Number,
      required: true,
    },
    artikel: {
      type: Object,
      required: true,
    },
    artikelNr: {
      type: String,
      required: true,
    },
    isInitialArticle: {
      type: Boolean,
      required: true,
    },
    dimension: {
      type: String,
      required: true,
    },
    isVspEnabled: {
      type: Boolean,
      required: false,
    },
  },

  setup(props) {
    const {
      isInitialArticle,
      artikelNr,
    } = toRefs(props);
    const addToCartButton = ref<HTMLElement | null>(null);

    function addToCartAfterSizeSelection() {
      nextTick(() => {
        if (props.artikel.outOfStock) {
          return;
        }
        emitCartContent();
      });
      EventBus.off('bewerten:vue:layer:hide', addToCartAfterSizeSelection);
    }

    function openSizeLayerForAddToCart(overrideEventContext: string | undefined = undefined) {
      EventBus.emit(
        'bewerten:vue:dimension:select',
        {
          dimension: props.dimension,
          add2CartAfterSizeSelection: true,
          overrideEventContext,
        },
      );
      EventBus.on('bewerten:vue:layer:hide', addToCartAfterSizeSelection);
      sendTrackingInfo(['click', 'cart_sizelayer']);
    }

    function addToCart() {
      if (isInitialArticle.value) {
        openSizeLayerForAddToCart();
      } else if (artikelNr.value) {
        emitCartContent();
      }
    }

    function getA2CPayload() {
      const payload: { detail: AddToCartPayload } = {
        detail: {
          artikel: getVerkaufId(),
          menge: 1,
        },
      };
      const vertriebsinfoId = getVertriebsinfoId();
      if (vertriebsinfoId) {
        payload.detail.vertriebsinfoId = vertriebsinfoId;
      }
      return payload;
    }

    // this method is used for mocking in unit tests
    function getVerkaufId() {
      return props.artikel.verkaufId;
    }

    // this method is used for mocking in unit tests
    function getVertriebsinfoId() {
      return props.artikel.vertriebsinfoId;
    }

    function emitCartContent() {
      // see: https://brix.dev.breuni.de/kaufen/sandbox/ssi.html
      const payload = getA2CPayload();
      const customKaufCartAddEvent = new CustomEvent('kaufen:cart:add', payload);
      document.dispatchEvent(customKaufCartAddEvent);
      console.info('Article was handed over to cart:', payload);
      addToCartButton.value?.setAttribute('data-test-bewerten-warenkorbbutton-payload-emitted', JSON.stringify(payload));
      window.performance.mark('bewe.pds.warenkorbbutton.payload-emitted');
    }

    function sendTrackingInfo(eventValues: ('click' | 'cart_sizelayer')[]) {
      track({
        event_level1: 'ents',
        event_level2: null,
        event_level3: null,
        event_level4: null,
        event_level5: null,
        event: 'event.generic',
        event_values: eventValues,
      });
    }

    function a2cClick() {
      if (props.artikel.outOfStock) {
        handleOutOfStockVariant();
      } else {
        addToCart();
      }
    }

    function getVspPayload() {
      return {
        syan: props.syan,
        ...(props.artikel.colors?.aktiv?.farbId ? {farbId: props.artikel.colors.aktiv.farbId} : {}),
        listName: 'pds_cta',
      };
    }

    function vspClick() {
      const payload = getVspPayload();
      document.dispatchEvent(new CustomEvent('suchen:vsp:alternativeprodukte', {detail: payload}));
      console.info('Visually similar products are displayed:', payload);
      addToCartButton.value?.setAttribute('data-test-ents-vsp-cta-payload-emitted', JSON.stringify(payload));
    }

    function handleOutOfStockVariant() {
      if (isInitialArticle.value) {
        EventBus.emit('bewerten:vue:dimension:select', {dimension: props.dimension});
      } else {
        EventBus.emit('bewerten:out-of-stock-layer:show', getVertriebsinfoId());
      }
    }

    async function addToCartFromEvent(e: CustomEvent<AddToCartEventDetail>) {
      if (!e.detail.articleId && props.dimension === 'groessen') {
        openSizeLayerForAddToCart(e.detail.eventContext);
        return;
      }

      EventBus.emit('bewerten:vue:artikel:changed', e.detail.articleId);
      emitCartContent();
    }

    onMounted(() => {
      document.addEventListener('ents:article:addtocart', addToCartFromEvent); // addtocart dispatched by BRTN
    });

    onBeforeUnmount(() => {
      document.removeEventListener('ents:article:addtocart', addToCartFromEvent);
    });

    return {
      a2cClick,
      vspClick,
      warenkorbbutton: addToCartButton,
    };
  },
});
